import styles from "../styles.module.scss";
import { useAppData } from "../../context/Contexts";
import { useQuery } from "@tanstack/react-query";
import React, { useState, useEffect } from "react";
import { preloadModelsForYear, awaitMakesSeed, awaitModelsSeed } from "../../services/SeedPrefetchService";
import fetchCheck from "../../services/CheckService";
import useCustomProductQuery from "../../services/CustomProductService";
import {getApiBase, MODEL_SORT_ORDER, slugify, resolveAvailabilitySeatKey, sanitizeHtml, withPartNumberSkuQuery} from "../../helpers/helpers";
import SuperOffer from "../SuperOffer";
import { useYearsQuery } from "../../services/YearsService";
import { withIDBFallback, IDB_STORES, IDB_TTL } from "../../services/IDBCacheService";

function getDefaultModel(
  currentAppData,
  model: string | undefined | null,
  models,
) {
  let model1 = model;
  if (currentAppData?.modelTemp) {
    model1 = currentAppData?.modelTemp;
  }
  if (window.scs_force_custom) {
    return "";
  }
  //console.log("model1:", model1);
  return model1;

  if (model1 === null || model1 === undefined) {
    return "";
  }
  if (!(models && models.data)) {
    return "";
  }
  if (typeof models.data[model1] !== "undefined") {
    return model1;
  }
  return "";
}

const visuallyHiddenStyle: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};

export default function Step1(props) {
  const { currentAppData, setCurrentAppData } = useAppData();
  const [disabledMakes, setDisabledMakes] = useState<boolean>(true);
  const [disabledModels, setDisabledModels] = useState<boolean>(true);
  const [showTrims, setShowTrims] = useState<boolean>(false);
  const [trims, setTrims] = useState<object>({});
  const [canMoveToStep2, setCanMoveToStep2] = useState<boolean>(false);

  const {
    custom_status,
    custom_data,
    custom_error,
    custom_isFetching,
    custom_refetch,
  } = useCustomProductQuery();

  let type = "normal";
  if (window?.is_real_truck) {
    type = "realtruck";
  } else if (window?.scs_force_custom) {
    type = "custom";
  } else if (window?.scs_custom_endpoint) {
    type = "custom_endpoint";
  }

  const fetchMakes = async () => {
    const year = currentAppData?.year;
    if (!year) return [];
    const withSales = !!window.scs_use_optgroup;
    const idbKey = `makes_${year}_${type}${withSales ? "_ws" : ""}`;

    await awaitMakesSeed();

    const makes = await withIDBFallback(
      IDB_STORES.MAKES,
      idbKey,
      IDB_TTL.MAKES,
      async () => {
        let query = `?year=${year}&type=${type}`;
        if (withSales) query += `&with_sales=yes`;
        const url = withPartNumberSkuQuery(`${getApiBase()}/api/v1/years` + query);
        const response = await fetch(url, {
          mode: "cors",
        });
        if (!response.ok) throw new Error(`Makes fetch failed: ${response.statusText}`);
        const data = await response.json();
        // Normalise both array and optgroup formats into a flat array for storage
        return Array.isArray(data.makes) ? data.makes : Object.values(data.makes).flat();
      }
    );

    if ((makes as string[]).length > 0) setDisabledMakes(false);
    if (withSales) return makes;
    return (makes as string[]).slice().sort();
  };
  const fetchModels = async () => {
    const year = currentAppData?.year;
    const make = currentAppData?.make;
    if (!year) return [];
    if (!make) return [];
    const withSales = !!window.scs_use_optgroup;
    const idbKey = `models_${year}_${make}_${type}${withSales ? "_ws" : ""}`;

    await awaitModelsSeed(year);

    // Store the raw { models, trims2 } object so both models list and
    // trims side-effect can be derived from a single IDB entry.
    const raw = await withIDBFallback(
      IDB_STORES.MODELS,
      idbKey,
      IDB_TTL.MODELS,
      async () => {
        let query = `?year=${year}&make=${make}&type=${type}`;
        if (withSales) query += `&with_sales=yes`;
        const url = withPartNumberSkuQuery(`${getApiBase()}/api/v1/years` + query);
        const response = await fetch(url, {
          mode: "cors",
        });
        if (!response.ok) throw new Error(`Models fetch failed: ${response.statusText}`);
        const data = await response.json();
        return { models: data.models, trims2: data.trims2 ?? null };
      }
    );

    // Side-effects always run regardless of whether data came from network or IDB
    if (Object.values(raw.models).length > 0) setDisabledModels(false);
    setTrims(raw.trims2 ?? null);
    setCurrentAppData((prevState) => ({ ...prevState, trims: raw.trims2 ?? null }));

    if (raw.trims2) {
      const queryParameters = new URLSearchParams(window.location.search);
      let model = getDefaultModel(
        currentAppData,
        window.sessionStorage.getItem("user_model") ?? null,
        { data: raw.models },
      );
      if (queryParameters.get("user_model")) {
        model = getDefaultModel(currentAppData, queryParameters.get("user_model") ?? null, { data: raw.models });
      }
      if (queryParameters.get("model")) {
        model = getDefaultModel(currentAppData, queryParameters.get("model") ?? null, { data: raw.models });
      }
      if (model && model !== "") {
        if (Object.keys(raw.trims2).length === 0) {
          setCanMoveToStep2(true);
        } else {
          setCanMoveToStep2(raw.trims2[model] === undefined);
        }
      }
    }

    if (withSales) return Object.values(raw.models);
    return Object.values(raw.models).sort();
  };

  // useEffect(() => {
  //     if (currentAppData?.trims) {
  //         const queryParameters = new URLSearchParams(window.location.search)
  //
  //         let model = window.sessionStorage.getItem("user_model") ?? null;
  //         if (queryParameters.get("user_model")) {
  //             model = queryParameters.get("user_model") ?? null;
  //         }
  //         if (queryParameters.get("model")) {
  //             model = queryParameters.get("model") ?? null;
  //         }
  //
  //         const tt = getTrims(model);
  //         if (model && tt.length === 0) {
  //
  //             setTimeout(function () {
  //                 let newStep = 2;
  //                 setCurrentAppData(prevState => {
  //                     return {...prevState, step: newStep}
  //                 });
  //             }, 1500)
  //
  //         }
  //     }
  // }, [currentAppData?.trims]);

  useEffect(() => {
    const tt = getTrims(currentAppData?.model);
    runCheckNow(
      currentAppData?.model,
      tt.length == 1 ? tt[0] : "",
      tt.length > 0,
    );
  }, [trims]);

  const yearsQuery = useYearsQuery();

  const makesQuery = useQuery({
    queryKey: [`makes-${currentAppData?.year}`],
    queryFn: fetchMakes,
  });

  const modelsQuery = useQuery({
    queryKey: [`models-${currentAppData?.year}-${currentAppData?.make}`],
    queryFn: fetchModels,
  });

  // fetchMakes/fetchModels only enable these selects and populate local `trims`
  // as one-time side effects inside their queryFn. If this component remounts
  // while react-query already has fresh/cached data for the current key, the
  // queryFn doesn't re-run — so that side effect never fires again and the
  // selects/trim-selector can get stuck showing stale local state even though
  // the data is right there in the query cache / currentAppData.trims. Resync
  // from that remount-resistant data on every render where it changes instead.
  useEffect(() => {
    if (makesQuery.data && makesQuery.data.length > 0) setDisabledMakes(false);
  }, [makesQuery.data]);

  useEffect(() => {
    if (modelsQuery.data && modelsQuery.data.length > 0) setDisabledModels(false);
  }, [modelsQuery.data]);

  useEffect(() => {
    if (!currentAppData?.model || !currentAppData?.trims) return;
    setTrims(currentAppData.trims);
    const modelTrims = currentAppData.trims[currentAppData.model];
    setShowTrims(Array.isArray(modelTrims) && modelTrims.length > 0);
  }, [currentAppData?.trims, currentAppData?.model]);

  const handleYearChange = (e) => {
    preloadModelsForYear(e.target.value);
    setTrims([]);
    setShowTrims(false);
    setCurrentAppData((prevState) => {
      return {
        ...prevState,
        year: e.target.value,
        make: "",
        model: "",
        trim: "",
        has_trims: false,
        has_cabs: false,
        step: 1,
        check: undefined
      };
    });
  };

  const handleMakeChange = (e) => {

    setTrims([]);
    setShowTrims(false);
    setCurrentAppData((prevState) => {
      return {
        ...prevState,
        make: e.target.value,
        model: "",
        trim: "",
        has_trims: false,
        has_cabs: false,
        step: 1
      };
    });
  };

  async function runCheckNow(
    value: string | undefined,
    trim: string | undefined,
    has_trims: boolean,
  ) {
    const check = await runCheck(value, trim);
    // console.log('runCheckNow', value, trim, has_trims)
    // console.log('runCheckNow trims', trims);
    // console.log('runCheckNow trims', getTrims(value).length > 0, hasCabSizes(value));

    if (
      check.compatibility_custom !== "N" &&
      window.scs_force_normal !== true &&
      window.is_real_truck !== true
    ) {
      //alert('Need to get custom product via graphql and show the options for custom product');
      if (window.scs_force_custom !== true) {
        const product = await getCustomProduct(value, "");
        setCurrentAppData((prevState) => {
          return {
            ...prevState,
            product: product,
            check: check,
            trim: trim,
            has_trims: getTrims(value).length > 0,
            has_cabs: hasCabSizes(value),
          };
        });
      }
      //product.images = currentAppData?.originalProduct?.images;
      setCurrentAppData((prevState) => {
        return {
          ...prevState,
          check: check,
          trim: trim,
          has_trims: getTrims(value).length > 0,
          has_cabs: hasCabSizes(value),
        };
      });
      return;
    }
    setCurrentAppData((prevState) => {
      return {
        ...prevState,
        check: check,
        trim: trim,
        has_trims: getTrims(value).length > 0,
        has_cabs: hasCabSizes(value),
      };
    });
    return;
  }

  // Local `trims` state is synced from currentAppData.trims by a separate
  // effect and can still be stale in a given closure (e.g. right after
  // `model` changes via slug detection, before that effect has run). Fall
  // back to the canonical currentAppData.trims so callers get the right
  // answer on the first pass instead of racing with that sync effect.
  function resolveModelTrims(model) {
    if (typeof trims[model] !== "undefined") {
      return trims[model];
    }
    if (currentAppData?.trims && typeof currentAppData.trims[model] !== "undefined") {
      return currentAppData.trims[model];
    }
    return undefined;
  }

  function hasCabSizes(model) {
    const modelTrims = resolveModelTrims(model);
    if (typeof modelTrims === "undefined") {
      return false;
    }
    return (
      [
        ...new Set(
          modelTrims.map((item) => item.cab).filter((item) => item != ""),
        ),
      ].length > 0
    );
  }

  function getCabSizes(model, trim) {
    const modelTrims = resolveModelTrims(model);
    if (typeof modelTrims === "undefined") {
      return [];
    }
    return [
      ...new Set(
        modelTrims
          .filter((item) => item.trim == trim)
          .map((item) => item.cab),
      ),
    ];
  }

  async function runCheckNowWithCabSize(
    value: string | undefined,
    trim: string | undefined,
    cabSize: string | undefined,
    has_trims: boolean,
    has_cab_sizes: boolean,
  ) {
    const check = await runCheck(value, trim, cabSize);
    if (
      check.compatibility_custom !== "N" &&
      window.scs_force_normal !== true &&
      window.is_real_truck !== true
    ) {
      //alert('Need to get custom product via graphql and show the options for custom product');
      if (window.scs_force_custom !== true) {
        const product = await getCustomProduct(value, "");
        setCurrentAppData((prevState) => {
          return {
            ...prevState,
            product: product,
            check: check,
            trim: trim,
            has_trims: has_trims,
            cab: cabSize,
            has_cabs: hasCabSizes(currentAppData.model),
          };
        });
      }
      //product.images = currentAppData?.originalProduct?.images;
      setCurrentAppData((prevState) => {
        return {
          ...prevState,
          check: check,
          trim: trim,
          has_trims: has_trims,
          cab: cabSize,
          has_cabs: hasCabSizes(currentAppData.model),
        };
      });
      return;
    }
    setCurrentAppData((prevState) => {
      return {
        ...prevState,
        check: check,
        trim: trim,
        has_trims: has_trims,
        cab: cabSize,
        has_cabs: hasCabSizes(currentAppData.model),
      };
    });
    return;
  }

  async function runTrims(mm) {
    // console.log('mm:', mm);
    if (trims && typeof trims[mm] !== "undefined" && trims[mm].length === 1) {
      await runCheckNow(mm, trims[mm][0].trim, true);
    }
  }

  function getTrims(model) {
    const modelTrims = resolveModelTrims(model);
    if (typeof modelTrims === "undefined") {
      return [];
    }
    return [
      ...new Set(
        modelTrims.map((item) => item.trim).filter((item) => item != ""),
      ),
    ].sort();
  }

  useEffect(() => {
    if (!canMoveToStep2) return;
    const timerId = setTimeout(function () {
      const newStep = 2;
      setCurrentAppData((prevState) => {
        return { ...prevState, step: newStep };
      });
    }, 1000);
    return () => clearTimeout(timerId);
  }, [canMoveToStep2]);


  useEffect(() => {
          if (currentAppData === null) {
              return;
          }
          let product1: any;
          if (currentAppData.check && currentAppData.check.compatibility === 'N') {
              product1 = currentAppData.product;
          } else {
              product1 = currentAppData.originalProduct;
          }
          // if (product1 && product1.options) {
          //     setProduct(product1);
          //     setOptions(product1.options.filter(option => option.name === 'Seats'));
          //     // console.log('product options', options);
          // }
          if (currentAppData.check && typeof currentAppData.check.availability !== 'undefined') {
              // currentAppData.seat is the raw Shopify option value — on universal
              // (see isUniversalBundle) vehicles that's "Front and Universal Rear Seats"/
              // "Universal Rear Seats Only" rather than "Bundle"/"Universal Back Bench",
              // so resolve through the alias table instead of indexing directly
              // (see helpers.tsx) — otherwise this always falls back to the first
              // available seat, even though the selected one really is available.
              const availKey = resolveAvailabilitySeatKey(currentAppData?.seat, currentAppData.check.availability);
              let enabled = currentAppData.check.availability[availKey]
                  && Object.keys(currentAppData.check.availability[availKey])
                      .filter(v => currentAppData?.check?.availability[availKey][v] === true).length > 0;
              if (currentAppData?.check.compatibility === 'F') {
                  if (currentAppData?.seat !== 'Front Seats') {
                      enabled = false;
                  }
              }
              if (!enabled) {
                  const availability = currentAppData?.check.availability ?? {};

                  const firstActiveAvailabilityKey = Object.entries(availability)
                      .find(([, options]) =>
                          Object.values(options).some(value => value === true)
                      )?.[0];

                  if (firstActiveAvailabilityKey) {
                      setNewSeatValue(firstActiveAvailabilityKey, firstActiveAvailabilityKey);
                  }
              }
          }

      }, [currentAppData?.check, currentAppData?.product, currentAppData?.originalProduct])

      const setNewSeatValue = async (new_value: string, new_seat_value: string) => {
              setCurrentAppData(prevState => {
                  return { ...prevState, seat: new_seat_value, isFetchingCheck: true }
              })

              const check = await fetchCheck(currentAppData?.year, currentAppData?.make, currentAppData?.model, currentAppData?.trim,
                  currentAppData?.color, new_seat_value, currentAppData?.cab, 'change_seat');
              setCurrentAppData(prevState => {
                  return { ...prevState, check: check, isFetchingCheck: false };
              });
          }


  async function runHandleModelChange(value) {
    await setCurrentAppData((prevState) => {
      return { ...prevState, model: value, trim: "" };
    });

    if (currentAppData?.year && currentAppData?.make && value) {
      /*if (
        window.location.href.includes("seatcoversolutions.com") &&
        !window.location.href.includes("/pages/jeep-landing-page") &&
        currentAppData?.make === "Jeep" &&
        ["Wrangler 2-Door", "Wrangler 4-Door"].includes(value)
      ) {
        window.location.href = `https://www.seatcoversolutions.com/pages/jeep-landing-page?make=${currentAppData?.make}&model=${value}&year=${currentAppData?.year}`;
        return;
      }*/

      //console.log("trims", trims);

      //DONE: Run compat check on backend and save compatibility information
      // console.log(
      //   "trims check",
      //   value,
      //   typeof trims[value] !== "undefined",
      //   trims[value],
      // );
      const modelTrims = resolveModelTrims(value);
      if (modelTrims && modelTrims.length > 0) {
        setCurrentAppData((prevState) => {
          const ttrims = getTrims(value);

          return {
            ...prevState,
            has_trims: ttrims.length > 0,
            has_cabs: hasCabSizes(value),
          };
        });
        setShowTrims(true);

        setTimeout(async function () {
          await runTrims(value);
        }, 10);
      } else {
        await runCheckNow(value, "", false);
      }
      return;
    }

    setCurrentAppData((prevState) => {
      return { ...prevState, check: undefined, trim: "", has_trims: false };
    });
  }

  const handleModelChange = async (e) => {
    setCurrentAppData(prevState => {
      return { ...prevState, step: 1 };
    })
    runHandleModelChange(e.target.value);
  };

  const handleTrimChange = async (e) => {
    setCurrentAppData((prevState) => {
      const cabSizes = getCabSizes(currentAppData?.model, e.target.value);
      return { ...prevState, cab: cabSizes.length == 1 ? cabSizes[0] : "" };
    });
    await runCheckNow(currentAppData?.model, e.target.value, true);
    //
    // const check = await runCheck(currentAppData?.model, e.target.value);
    // if (check.compatibility === 'N') {
    //     //alert('Need to get custom product via graphql and show the options for custom product');
    //     const product = await getCustomProduct(currentAppData?.model, e.target.value);
    //     product.images = currentAppData?.originalProduct?.images;
    //     await setCurrentAppData(prevState => {
    //         return { ...prevState, product: product }
    //     });
    //     return;
    // }
  };
  const handleCabSizeChange = async (e) => {
    await runCheckNowWithCabSize(
      currentAppData?.model,
      currentAppData?.trim,
      e.target.value,
      true,
      true,
    );
  };

  const handleClickChange = (e) => {
    setTrims([]);
    setShowTrims(false);
    setCurrentAppData((prevState) => {
      return {
        ...prevState,
        step: 1,
        year: "",
        make: "",
        model: "",
        trim: "",
        cab: "",
        has_trims: false,
        has_cabs: false,
        seat: "",
        color: "Black",
        addons: "",
        check: undefined,
      };
    });
  };

  const runCheck = async (model, trim, cabSize = "") => {
    setCurrentAppData((prevState) => ({ ...prevState, isFetchingCheck: true }));
    try {
      const check = await fetchCheck(
        currentAppData?.year,
        currentAppData?.make,
        model,
        trim,
        currentAppData?.color,
        currentAppData?.seat,
        cabSize,
      );
      // console.log(`check result ${currentAppData?.year} ${currentAppData?.make} ${model} ${trim}`, check);
      let newStep = currentAppData?.step;
      if (
        (check.compatibility === "A" || check.compatibility === "N") &&
        check.compatibility_custom === "N"
      ) {
        newStep = 3;
        await setCurrentAppData((prevState) => {
          return {
            ...prevState,
            check: check,
            trim: trim,
            step: newStep,
            has_cabs: hasCabSizes(currentAppData.model),
            isFetchingCheck: false,
          };
        });
        return check;
      }
      await setCurrentAppData((prevState) => {
        return {
          ...prevState,
          check: check,
          trim: trim,
          has_cabs: hasCabSizes(currentAppData.model),
          isFetchingCheck: false,
        };
      });
      return check;
    } catch (e) {
      setCurrentAppData((prevState) => ({ ...prevState, isFetchingCheck: false }));
      throw e;
    }
  };

  const getCustomProduct = (value: string | undefined, trim: string) => {
    custom_refetch();
    // console.log('getCustomProduct data', custom_data);

    return custom_data;
  };

  useEffect(() => {
    if (!currentAppData?.model) return;

    // console.log('useEffect model');

    async function runEffect() {
      if (
        currentAppData?.year &&
        currentAppData?.make &&
        currentAppData?.model
      ) {
        await runHandleModelChange(currentAppData?.model);
        // showTrims is derived from currentAppData.trims/model in the sync
        // effect above, which reads canonical state rather than the `trims`
        // closure captured when this effect instance was scheduled. Setting
        // it here too raced with that effect: this effect re-fires once for
        // the model change (with `trims` possibly still stale) and again
        // once `trims` catches up, and the two async runs can resolve out of
        // order, letting the stale run's setShowTrims(false) win last.
        return;
      }
      setCurrentAppData((prevState) => {
        return { ...prevState, check: undefined, trim: "", has_trims: false };
      });
    }

    runEffect();
  }, [currentAppData?.model, trims]);

  /*useEffect(() => {
        if (!currentAppData?.trim || currentAppData?.trim === '')
            return;

        runCheckNow(currentAppData?.model, currentAppData?.trim, true);

    }, [currentAppData?.trim]);*/

  // console.log('trims', trims, currentAppData?.model, showTrims, trims && currentAppData?.model && showTrims)

    // console.log(
    //     'makesQuery', makesQuery, makesQuery?.data, disabledMakes
    // );

  return (<>

    <section
      className={`${styles.section__step} ${window.scs_custom_style ? styles.style01 : ""} ${currentAppData?.step == 1 ? styles.section_active : ""} 
      ${window.scs_black_friday_2025 ? styles.has_bf_2025: ''}`}
      id="step_1"
    >
      <h2>
        Step 1/3 - <strong>Vehicle Details</strong>
      </h2>
      {currentAppData?.step === 1? (
        <>
            <SuperOffer />
          <div
            className={`${styles.vehicle_selector} ${showTrims ? styles.has_trims : ""}`}
          >
            <label className={styles.sr_only} htmlFor={`${props.id}-vehicle-year`} style={visuallyHiddenStyle}>
              Vehicle year
            </label>
            <select
                id={`${props.id}-vehicle-year`}
                name="vehicle-year"
              onChange={handleYearChange}
              value={currentAppData?.year}
              data-selected={currentAppData?.year}
            >
              <option value="">Year</option>
              {yearsQuery.data &&
                yearsQuery.data.map((year, i) => {
                  return (
                    <option
                      key={`opt-${props.id}-${year}`}
                      value={`${slugify(year)}`}

                    >
                      {year}
                    </option>
                  );
                })}
            </select>
            <label className={styles.sr_only} htmlFor={`${props.id}-vehicle-make`} style={visuallyHiddenStyle}>
              Vehicle make
            </label>
            <select
                id={`${props.id}-vehicle-make`}
                name="vehicle-make"
              onChange={handleMakeChange}
              disabled={disabledMakes}
              value={currentAppData?.make}
              data-selected={currentAppData?.make}
            >
              <option value="">Make</option>
              {window.scs_use_optgroup ? (
                <>
                  <optgroup label="Top Sellers">
                    {makesQuery &&
                      makesQuery.data &&
                      makesQuery.data
                        .filter((v) => v.trim() != "")
                        .slice(0, 6)
                        .map((make, i) => {
                          return (
                            <option
                              key={`opt-${slugify(make)}`}
                              value={`${make}`}

                            >
                              {make}
                            </option>
                          );
                        })}
                  </optgroup>
                  <optgroup label="All Others">
                    {makesQuery &&
                      makesQuery.data &&
                      makesQuery.data
                        .filter((v) => v.trim() != "")
                        .slice(6)
                        .map((make, i) => {
                          return (
                            <option
                              key={`opt-${props.id}-${slugify(make)}`}
                              value={`${make}`}

                            >
                              {make}
                            </option>
                          );
                        })}
                  </optgroup>
                </>
              ) : (
                makesQuery &&
                makesQuery.data &&
                makesQuery.data.map((make, i) => {
                  return (
                    <option
                      key={`opt-${slugify(make)}`}
                      value={`${make}`}

                    >
                      {make}
                    </option>
                  );
                })
              )}
            </select>
            <label className={styles.sr_only} htmlFor={`${props.id}-vehicle-model`} style={visuallyHiddenStyle}>
              Vehicle model
            </label>
            <select
                id={`${props.id}-vehicle-model`}
                name="vehicle-model"
              onChange={handleModelChange}
              disabled={disabledModels}
              value={currentAppData?.model}
            >
              <option value="">Model</option>
              {window.scs_use_optgroup && MODEL_SORT_ORDER[currentAppData?.make] ? (
                <>
                  <optgroup label="Top Sellers">
                    {modelsQuery &&
                      modelsQuery.data &&
                      modelsQuery.data.slice(0, Object.keys(MODEL_SORT_ORDER[currentAppData?.make]).length).map((model, i) => {
                        return (
                          <option
                            key={`opt-${slugify(model)}`}
                            value={`${model}`}

                          >
                            {model}
                          </option>
                        );
                      })}
                  </optgroup>
                  <optgroup label="All Others">
                    {modelsQuery &&
                      modelsQuery.data &&
                      modelsQuery.data.slice(Object.keys(MODEL_SORT_ORDER[currentAppData?.make]).length).map((model, i) => {
                        return (
                          <option
                            key={`opt-${slugify(model)}`}
                            value={`${model}`}

                          >
                            {model}
                          </option>
                        );
                      })}
                  </optgroup>
                </>
              ) : (
                modelsQuery &&
                modelsQuery.data &&
                modelsQuery.data.map((model, i) => {
                  return (
                    <option
                      key={`opt-${slugify(model)}`}
                      value={`${model}`}

                    >
                      {model}
                    </option>
                  );
                })
              )}
            </select>
            {trims && currentAppData.model && showTrims ? (
              <>
              <label className={styles.sr_only} htmlFor="vehicle-trim" style={visuallyHiddenStyle}>
                Vehicle trim
              </label>
              <select id={`vehicle-trim`} onChange={handleTrimChange}
                      value={currentAppData?.trim}>
                <option value="">Trim</option>
                {trims &&
                  typeof trims[currentAppData.model] !== "undefined" &&
                  getTrims(currentAppData?.model).map((obj, i) => {
                    const trim = obj;
                    return (
                      <option
                        key={`opt-${slugify(trim)}`}
                        value={`${trim}`}

                      >
                        {trim}
                      </option>
                    );
                  })}
              </select>
              </>
            ) : (
              ""
            )}
            {trims &&
            currentAppData.model &&
            showTrims &&
            hasCabSizes(currentAppData.model) ? (
              <>
              <label className={styles.sr_only} htmlFor="vehicle-cab-size" style={visuallyHiddenStyle}>
                Vehicle cab size
              </label>
              <select id={`vehicle-cab-size`} onChange={handleCabSizeChange}
                      value={currentAppData?.cab}>
                <option value="">Cab Size</option>
                {trims &&
                  typeof trims[currentAppData.model] !== "undefined" &&
                  getCabSizes(currentAppData?.model, currentAppData?.trim).map(
                    (cabSize, i) => {
                      return (
                        <option
                          key={`opt-${slugify(cabSize)}`}
                          value={`${cabSize}`}

                        >
                          {cabSize}
                        </option>
                      );
                    },
                  )}
              </select>
              </>
            ) : (
              ""
            )}
          </div>
          {!window.scs_force_custom && currentAppData.check && currentAppData.check?.alert ? (
            <div className={`${styles.vehicle_alert}`}>
              <strong>Warning:</strong> {currentAppData.check?.alert}
            </div>
          ) : (
            ""
          )}
          {window.scs_step_1_bottom_text ? (
            <div
              className={`${styles.bottom_text}`}
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(window.scs_step_1_bottom_text),
              }}
            ></div>
          ) : (
            ""
          )}
        </>
      ) : (
        <div className={`${styles.summary}`}>
          <div
            className={`${styles.selected_value}`}
          >{`${currentAppData?.year} ${currentAppData?.make} ${currentAppData?.model}${currentAppData?.trim ? " " + currentAppData?.trim : ""}${currentAppData?.cab ? " " + currentAppData?.cab : ""}`}</div>
          <button
            className={`${styles.btn_change}`}
            onClick={handleClickChange}
          >
            Change Vehicle
          </button>
        </div>
      )}
    </section></>
  );
}
